import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Squares2X2Icon,
  HeartIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
  CogIcon,
  BellIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../context/WalletContext';

const sidebarItems = [
  {
    section: 'General',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: Squares2X2Icon },
      { name: 'Notificaciones', href: '/dashboard/notifications', icon: BellIcon },
    ],
  },
  {
    section: 'Mis Actividades',
    items: [
      { name: 'Mis Intereses', href: '/dashboard/watchlist', icon: HeartIcon },
      { name: 'Mis Pujas', href: '/dashboard/bids', icon: ChartBarIcon },
      { name: 'Mis Compras', href: '/dashboard/purchases', icon: ShoppingBagIcon },
      { name: 'Mis Facturas', href: '/dashboard/invoices', icon: DocumentTextIcon },
    ],
  },
  {
    section: 'Participación',
    items: [
      { name: 'Mi Staking', href: '/dashboard/staking', icon: CurrencyDollarIcon },
      { name: 'Gobernanza', href: '/dashboard/governance', icon: ShieldCheckIcon },
    ],
  },
  {
    section: 'Vender',
    items: [
      { name: 'Crear Subasta', href: '/dashboard/create', icon: PlusCircleIcon },
    ],
  },
  {
    section: 'Cuenta',
    items: [
      { name: 'Mi Perfil', href: '/dashboard/profile', icon: CogIcon },
    ],
  },
];

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const { address, ndhBalance, tier } = useWallet();

  const getTierColor = (t: string) => {
    switch (t) {
      case 'PLATINUM': return 'from-slate-300 to-indigo-400';
      case 'GOLD': return 'from-yellow-400 to-amber-500';
      case 'SILVER': return 'from-slate-400 to-slate-300';
      case 'BRONZE': return 'from-amber-700 to-amber-600';
      default: return 'from-dark-400 to-dark-500';
    }
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 fixed left-0 top-20 h-[calc(100vh-5rem)] bg-dark-900/50 border-r border-dark-800 overflow-y-auto">
          <div className="p-4">
            {/* User Card */}
            <div className="bg-dark-800/50 rounded-xl p-4 mb-6 border border-dark-700">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTierColor(tier)} flex items-center justify-center text-white font-bold`}>
                  {address ? address.slice(2, 4).toUpperCase() : 'U'}
                </div>
                <div>
                  <p className="text-white font-medium text-sm">
                    {address ? formatAddress(address) : 'Wallet'}
                  </p>
                  <p className={`text-xs px-2 py-0.5 rounded bg-gradient-to-r ${getTierColor(tier)} text-white inline-block`}>
                    {tier || 'Básico'}
                  </p>
                </div>
              </div>
              <div className="text-center py-2 bg-dark-900/50 rounded-lg">
                <p className="text-xs text-gray-500">Balance NDH</p>
                <p className="text-lg font-bold text-primary-400">
                  {parseFloat(ndhBalance).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Back to Home */}
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 mb-4 text-gray-400 hover:text-white hover:bg-dark-800 rounded-lg transition-colors text-sm"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Volver al inicio
            </Link>

            {/* Navigation */}
            <nav className="space-y-6">
              {sidebarItems.map((section) => (
                <div key={section.section}>
                  <p className="px-3 text-xs text-gray-500 uppercase tracking-wider mb-2">
                    {section.section}
                  </p>
                  <ul className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <li key={item.href}>
                          <Link
                            to={item.href}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                              isActive
                                ? 'bg-primary-500/10 text-primary-400 border-l-2 border-primary-500'
                                : 'text-gray-400 hover:text-white hover:bg-dark-800'
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            {item.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-dark-900/95 backdrop-blur-sm border-t border-dark-800 z-40">
        <div className="flex justify-around items-center h-16">
          {[
            { name: 'Home', href: '/dashboard', icon: Squares2X2Icon },
            { name: 'Pujas', href: '/dashboard/bids', icon: ChartBarIcon },
            { name: 'Compras', href: '/dashboard/purchases', icon: ShoppingBagIcon },
            { name: 'Staking', href: '/dashboard/staking', icon: CurrencyDollarIcon },
            { name: 'Perfil', href: '/dashboard/profile', icon: CogIcon },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center gap-1 px-3 py-2 ${
                  isActive ? 'text-primary-400' : 'text-gray-500'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
